import type { AutocompleteProps } from '@mui/material';
import type { BaseAutocompleteInputProps } from '../Autocomplete.types.ts';
import type { BaseAppointmentType } from './BaseAppointmentTypesAutocomplete.types.ts';

import { Autocomplete, TextField } from '@mui/material';

import { matchSorter } from '#lib/match-sorter.ts';
import { BASE_APPOINTMENT_TYPES } from '#routes/Home/Filters/AppointmentTypes.utils.ts';
import { getTextFieldProps } from '../Autocomplete.utils.ts';

interface BaseAppointmentTypesAutocompleteProps<
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
> extends Omit<
      AutocompleteProps<BaseAppointmentType, Multiple, DisableClearable, false>,
      | 'loading'
      | 'options'
      | 'getOptionLabel'
      | 'filterOptions'
      | 'isOptionEqualToValue'
      | 'onInputChange'
      | 'renderInput'
      | 'freeSolo'
    >,
    Omit<BaseAutocompleteInputProps, 'dataFilter'> {}

export function BaseAppointmentTypesAutocomplete<
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
>({
  inputName = 'base-appointment-type',
  inputLabel = 'Base Appointment Type',
  inputHelperText,
  inputError,
  inputRequired,
  inputSize = 'small',
  fullWidth = true,
  filterSelectedOptions = true,
  clearOnBlur = false,
  selectOnFocus = true,
  handleHomeEndKeys = true,
  multiple,
  ...props
}: BaseAppointmentTypesAutocompleteProps<
  Multiple,
  DisableClearable
>): JSX.Element {
  return (
    <Autocomplete
      freeSolo={false}
      multiple={multiple}
      fullWidth={fullWidth}
      clearOnBlur={clearOnBlur}
      selectOnFocus={selectOnFocus}
      handleHomeEndKeys={handleHomeEndKeys}
      loading={false}
      options={BASE_APPOINTMENT_TYPES}
      filterSelectedOptions={filterSelectedOptions}
      filterOptions={(options, { inputValue }) =>
        matchSorter(options, inputValue)
      }
      renderInput={(params) => (
        <TextField
          {...getTextFieldProps({ params, multiple, inputRequired })}
          name={inputName}
          label={inputLabel}
          helperText={inputHelperText}
          error={inputError}
          size={inputSize}
        />
      )}
      {...props}
    />
  );
}
